import React from "react";
//#region types
import { FormFieldProps, MultipleAutoCompleteExtraProps } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import usePlaceholder from "../hooks/usePlaceholder";
import MultipleAutoCompleteInput from "../baseFields/multipleAutoCompleteInput";
import ReadOnlyField from "../baseFields/readOnlyField";
import { FormControl } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

function MultipleAutoCompleteTextFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  React.useImperativeHandle(props.fieldRef, () => ({
    change: (values: AutocompleteListItem[]) => {
      onChangeCustom(values ?? []);
    },
  }));

  const { name, field, errors, value, handleOnChangeCustom, success, optional, readonly } = props;
  const hasError = !!errors;
  const extraProps = field.extraProps as MultipleAutoCompleteExtraProps;
  const placeholder = usePlaceholder(field.label, field.placeholder, optional);

  const onChangeCustom = (newValues: AutocompleteListItem[]) => {
    handleOnChangeCustom(name, newValues);
  };

  const component = React.useMemo(
    () => (
      <MultipleAutoCompleteInput
        label={LocalizationHelperInstance.getFormattedMessage(field.label)}
        value={value ?? []}
        name={name}
        items={extraProps?.items}
        suggestedItems={extraProps.suggestedItems}
        className={field.classes?.input}
        onItemsSelect={onChangeCustom}
        placeholder={placeholder}
        valueSuffix={extraProps.valueSuffix}
        error={hasError}
        success={success}
        actions={extraProps.actions}
        includeNone={extraProps.includeNone}
        disabled={props.disabled}
      />
    ),
    [onChangeCustom, errors, value, success, name, placeholder, extraProps]
  );

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href} className={cssExports.readOnlyTextField}>
      {value?.length
        ? value.map((v: AutocompleteListItem) => v.value).join(", ")
        : field.showNotSelectedTextWhenReadOnly
        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotSelected)
        : ""}
    </ReadOnlyField>
  ) : (
    <FormControl fullWidth className={clsx(cssExports.inputFormControl)}>
      {component}
    </FormControl>
  );
}

export default MultipleAutoCompleteTextFormField;
